import { type ReactNode, type ElementType } from "react";
import { clsx } from "clsx";

type FontFamily = "zen" | "antique" | "noto" | "rufina";
type TextSize = "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl";
type TextColor = "black" | "blue" | "red" | "white" | "gray" | "green-600";

const fontFamilyMap: Record<FontFamily, string> = {
  zen: "'Zen Kaku Gothic New', sans-serif",
  antique: "YakuHanJP, 'Zen Kaku Gothic Antique', sans-serif",
  noto: "'Noto Sans JP', sans-serif",
  rufina: "Rufina, serif",
};

const sizeMap: Record<TextSize, { mobile: string; desktop: string }> = {
  xs: { mobile: "text-xs", desktop: "md:text-sm" },
  sm: { mobile: "text-sm", desktop: "md:text-base" },
  base: { mobile: "text-sm", desktop: "md:text-base" },
  lg: { mobile: "text-base", desktop: "md:text-xl" },
  xl: { mobile: "text-xl", desktop: "md:text-2xl" },
  "2xl": { mobile: "text-2xl", desktop: "md:text-3xl" },
  "3xl": { mobile: "text-2xl", desktop: "md:text-4xl" },
  "4xl": { mobile: "text-3xl", desktop: "md:text-5xl" },
  "5xl": { mobile: "text-4xl", desktop: "md:text-6xl" },
  "6xl": { mobile: "text-5xl", desktop: "md:text-7xl" },
};

const colorMap: Record<TextColor, string> = {
  black: "text-shegate-black",
  blue: "text-shegate-accent",
  red: "text-shegate-red",
  white: "text-white",
  gray: "text-gray-500",
  "green-600": "text-shegate-accent",
};

type TypographyProps = {
  children: ReactNode;
  as?: ElementType;
  font?: FontFamily;
  size?: TextSize;
  color?: TextColor;
  bold?: boolean;
  lineHeight?: number;
  letterSpacing?: string;
  className?: string;
};

export function Typography({
  children,
  as: Component = "span",
  font = "antique",
  size = "base",
  color = "black",
  bold = false,
  lineHeight = 1.8,
  letterSpacing,
  className,
}: TypographyProps) {
  const defaultLetterSpacing = font === "zen" || font === "antique" ? "1px" : "0.8px";

  return (
    <Component
      className={clsx(
        sizeMap[size].mobile,
        sizeMap[size].desktop,
        colorMap[color],
        bold && "font-bold",
        className
      )}
      style={{
        fontFamily: fontFamilyMap[font],
        lineHeight,
        letterSpacing: letterSpacing ?? defaultLetterSpacing,
      }}
    >
      {children}
    </Component>
  );
}

// Heading components for convenience
export function Heading1({
  children,
  color = "black",
  className,
}: {
  children: ReactNode;
  color?: TextColor;
  className?: string;
}) {
  return (
    <Typography
      as="h1"
      font="antique"
      size="4xl"
      color={color}
      bold
      letterSpacing="2.2px"
      className={className}
    >
      {children}
    </Typography>
  );
}

export function Heading2({
  children,
  color = "black",
  className,
}: {
  children: ReactNode;
  color?: TextColor;
  className?: string;
}) {
  return (
    <Typography
      as="h2"
      font="antique"
      size="2xl"
      color={color}
      bold
      letterSpacing="1.6px"
      className={className}
    >
      {children}
    </Typography>
  );
}

export function SubTitle({
  children,
  color = "blue",
  className,
}: {
  children: ReactNode;
  color?: TextColor;
  className?: string;
}) {
  return (
    <Typography
      as="p"
      font="rufina"
      size="lg"
      color={color}
      lineHeight={1.5}
      letterSpacing="1.2px"
      className={className}
    >
      {children}
    </Typography>
  );
}

export function BodyText({
  children,
  size = "base",
  color = "black",
  className,
}: {
  children: ReactNode;
  size?: TextSize;
  color?: TextColor;
  className?: string;
}) {
  return (
    <Typography
      as="p"
      font="antique"
      size={size}
      color={color}
      lineHeight={2}
      className={className}
    >
      {children}
    </Typography>
  );
}

export function AccentText({
  children,
  color = "blue",
  size = "xl",
  className,
}: {
  children: ReactNode;
  color?: TextColor;
  size?: TextSize;
  className?: string;
}) {
  return (
    <Typography
      as="span"
      font="antique"
      size={size}
      color={color}
      bold
      letterSpacing="1.3px"
      className={className}
    >
      {children}
    </Typography>
  );
}
